import { PermissionEnum } from '@Application/Security/Roles';
import { openDocumentTypeSidebarForRename } from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Signals/documentTypeSidebarSignal';
import { setDocumentTypeToArchive } from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Signals/documentTypeToArchiveSignal';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';
import type DocumentTypesListView from '@UI/Store/DocumentTypes/Views/DocumentTypesListView';
import { css } from '@emotion/react';
import { Archive, Pencil } from 'lucide-react';
import * as React from 'react';
import { Button } from '@verclam/ui-kit';

const actionsRowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.25rem;
`;

type Props = {
	readonly documentTypeId: number;
	/** Resolves whether the entry's own type is a system type — never rendered for one of the four. */
	readonly documentTypes: DocumentTypesListView;
};

/**
 * Leaf action component sitting next to a free document type's title, on the "Affectation par type"
 * tab: renders nothing without `DOCUMENT_TYPE_WRITE`, and nothing for one of the four system types,
 * which never expose renaming or archiving. Opens the rename sidebar and requests the archive
 * confirmation purely through their signals' setters — no callback prop.
 */
const DocumentTypeActions: React.FC<Props> = ({ documentTypeId, documentTypes }) => {
	const hasWritePermission = useHasPermission(PermissionEnum.DOCUMENT_TYPE_WRITE);
	const documentType = documentTypes.findById(documentTypeId);

	const handleRenameClick = React.useCallback(() => {
		openDocumentTypeSidebarForRename(documentTypeId);
	}, [documentTypeId]);

	const handleArchiveClick = React.useCallback(() => {
		setDocumentTypeToArchive(documentTypeId);
	}, [documentTypeId]);

	if (!hasWritePermission || documentType === null || documentType.isSystem) {
		return null;
	}

	return (
		<div css={actionsRowStyle}>
			<Button
				type="button"
				severity="secondary"
				outlined
				tooltip="Renommer le type de document"
				tooltipOptions={{ position: 'top' }}
				aria-label="Renommer le type de document"
				icon={<Pencil size={16} />}
				onClick={handleRenameClick}
			/>
			<Button
				type="button"
				severity="danger"
				outlined
				tooltip="Archiver le type de document"
				tooltipOptions={{ position: 'top' }}
				aria-label="Archiver le type de document"
				icon={<Archive size={16} />}
				onClick={handleArchiveClick}
			/>
		</div>
	);
};

export default DocumentTypeActions;
